import { parseInt } from 'lodash'
import { useCallback, useContext, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useToasts } from 'react-toast-notifications'
import { FileUploader } from 'src/applications/Embed/components/FileUploader'
import * as Layout from 'src/applications/Embed/elements/LayoutElements'
import { Header } from 'src/applications/Embed/fragments/Header'
import { EmbedLayout } from 'src/applications/Embed/fragments/Layout'
import { Loader } from 'src/applications/Embed/fragments/Loader'
import { IScenePayload, SCENE } from 'src/applications/Embed/types/IScene'
import { useUploadFile } from 'src/applications/Oversight/hooks/useUploadFile'
import { FileUploadContext } from 'src/contexts/FileUploadContext'
import { useMapDataSourceAndDataTemplateMutation } from 'src/queries/hooks/useMapDataSourceAndDataTemplateMutation'
import { LogoIcon } from 'src/resources/elements/Icons'
import { useDelayedRender } from 'src/resources/hooks/useDelayedRender'
import { getRoundedNumber } from 'src/resources/utils/getRoundedNumber'

import {
  RelativeFooter,
  HelpContent,
  IconContainer,
  EllipsisLoader,
  CancelButton
} from 'src/applications/Embed/elements/SceneElements'
import { FeatureFlagContext } from '../fragments/FeatureFlagContext'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'

const uploadingFileMessage = (
  <EllipsisLoader text={i18next.t('scenes.1_InitialScene.uploadingFile')} />
)

export const InitialScene = ({ batch, onCancelBatch, onComplete }: IScenePayload) => {
  const { addToast } = useToasts()
  const [loading, setLoading] = useState(false)
  const { workspaceId, schemaId, batchId: existingBatchId } = batch
  const uploadFile = useUploadFile(workspaceId, null, schemaId, existingBatchId)
  const [mapDataSource] = useMapDataSourceAndDataTemplateMutation()
  const features = useContext(FeatureFlagContext)
  const whitelabel = features.includes('V3_WHITE_LABEL')
  const { fileUploadState } = useContext(FileUploadContext)
  const { t } = useTranslation()
  const handleDrop = useCallback(async (file) => {
    try {
      setLoading(true)

      const res = await uploadFile.onUpload({ file })

      if (res instanceof Error) {
        throw res
      }

      const { batchId, viewId, uploadId } = res

      const {
        data: { mapDataSourceAndDataTemplate }
      } = await mapDataSource({
        variables: {
          viewId,
          uploadId,
          schemaId: parseInt(schemaId, 10)
        }
      })

      if (!mapDataSourceAndDataTemplate.success) {
        // TODO - figure out what these errors are and if/how to translate
        // basically, is this error what gets displayed a few lines down
        throw new Error('Failed during map data source.')
      }

      onComplete({
        uploadId,
        batchId
      })
    } catch ({ message }) {
      if (message.includes(': canceled')) {
        onCancelBatch()
      } else {
        addToast(message, {
          appearance: 'error',
          autoDismiss: true
        })
      }
    } finally {
      setLoading(false)
    }
  }, [])
  const uploadProgress = Object.entries(fileUploadState ?? {}).find(
    (uploadArray) => uploadArray?.[1].workspaceId === workspaceId
  )

  const percentLoaded = getRoundedNumber(uploadProgress?.[1].percent)
  const delayedRender = useDelayedRender(1500)

  const ConditionalLoader = () => {
    if (percentLoaded > 1) {
      return (
        <Loader
          percentage={percentLoaded}
          displayLoadingCircle
          message={uploadingFileMessage}
          fontSize={21}
        >
          <CancelButton onClick={uploadFile.cancel} />
        </Loader>
      )
    } else {
      return <Loader message={uploadingFileMessage} fontSize={21} />
    }
  }

  return (
    <EmbedLayout>
      <Layout.Content>
        <Layout.FlexVertical>
          {loading ? (
            delayedRender(ConditionalLoader)
          ) : (
            <>
              <Header scene={SCENE.INITIAL} />
              <FileUploader onDrop={handleDrop}>
                {!whitelabel && (
                  <RelativeFooter>
                    <IconContainer>
                      <LogoIcon />
                    </IconContainer>
                    {t('scenes.1_InitialScene.nonWhiteLabel', { company: 'Flatfile' })}
                  </RelativeFooter>
                )}
              </FileUploader>
            </>
          )}
        </Layout.FlexVertical>
      </Layout.Content>
      {batch.config?.helpContent && (
        <Layout.Sidebar>
          <HelpContent>
            <ReactMarkdown linkTarget={'_blank'}>{batch.config?.helpContent}</ReactMarkdown>
          </HelpContent>
        </Layout.Sidebar>
      )}
    </EmbedLayout>
  )
}
